// OS MOTIVOS DE PERDA — cadastro e relatorio na MESMA tela ([DA-671]).
//
// A lista e' curta (7 linhas) e nao merecia um item de menu proprio: mora atras do botao "Motivos
// de perda" da Gestao de Vendas. O que ela mostra ao lado de cada motivo — quantas vendas se
// perderam por ele e quanto isso deu em R$ — e' o relatorio que justifica o campo obrigatorio no
// botao "Perdeu". Sem esse numero aqui, o dado ficaria so no banco e ninguem o leria.
//
// ⛔ Nao ha lixeira: motivo se INATIVA. Orcamento antigo aponta pra linha e o nome e' lido de la;
// apagado, a leitura do passado fica em branco. Erro de digitacao se conserta renomeando.
import { useCallback, useEffect, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { Pencil, Plus } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { useToast } from '../../components/Toast';

// O que o modal do "Perdeu" precisa saber de um motivo. Vive aqui porque e' esta tela que manda
// no cadastro; a fila so consome.
export interface MotivoPerda {
  id: number;
  nome: string;
  exige_comentario: boolean;
  // Cor do rotulo ([DA-674]) — nula enquanto o admin nao pintou. Opcionais porque a tela de gestao
  // e o cadastro rapido montam o objeto sem elas.
  cor_fundo?: string | null;
  cor_fonte?: string | null;
}

interface LinhaGestao extends MotivoPerda {
  ordem: number;
  ativo: boolean;
  usos: string;
  valor: string;
}

interface Comentario {
  numero: string;
  total: string;
  comentario: string;
  motivo: string | null;
  vendedora: string | null;
  retomada_em: string;
}

const PERIODOS = [30, 90, 365];

export function ModalMotivosPerda({ ehAdmin, onFechar }: { ehAdmin: boolean; onFechar: () => void }) {
  const toast = useToast();
  const [linhas, setLinhas] = useState<LinhaGestao[]>([]);
  const [dias, setDias] = useState(90);
  const [carregando, setCarregando] = useState(true);
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<{ id: number; nome: string } | null>(null);
  const [comentarios, setComentarios] = useState<Comentario[] | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get<{ linhas: LinhaGestao[] }>('/api/motivos-perda', { query: { gestao: '1', dias } });
      setLinhas(r.linhas);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao carregar os motivos');
    } finally {
      setCarregando(false);
    }
  }, [dias, toast]);

  useEffect(() => { void carregar(); }, [carregar]);

  async function salvarNome() {
    if (!editando || editando.nome.trim().length < 3) return;
    try {
      await api.put(`/api/motivos-perda/${editando.id}`, { nome: editando.nome.trim() });
      setEditando(null);
      void carregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao renomear');
    }
  }

  async function alternarAtivo(m: LinhaGestao) {
    try {
      await api.put(`/api/motivos-perda/${m.id}`, { ativo: !m.ativo });
      toast.sucesso(m.ativo ? `${m.nome} saiu da lista` : `${m.nome} voltou pra lista`);
      void carregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao gravar');
    }
  }

  async function verComentarios() {
    if (comentarios) { setComentarios(null); return; }
    try {
      const r = await api.get<{ linhas: Comentario[] }>('/api/motivos-perda/comentarios', { query: { dias } });
      setComentarios(r.linhas);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao ler os comentarios');
    }
  }

  const totalUsos = linhas.reduce((s, l) => s + Number(l.usos), 0);
  const totalValor = linhas.reduce((s, l) => s + Number(l.valor), 0);

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura={760}
      titulo="Motivos de perda"
      rodape={
        <div className="flex items-center justify-between gap-3 w-full">
          <span className="text-xs text-gray-500">
            {totalUsos === 0
              ? 'Nenhuma venda marcada como perdida neste periodo.'
              : `${totalUsos} ${totalUsos === 1 ? 'venda perdida' : 'vendas perdidas'} · ${formatMoeda(totalValor)}`}
          </span>
          <button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>
        </div>
      }
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-1">
          {PERIODOS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDias(d)}
              className="text-xs px-2 py-1 rounded-lg"
              style={{
                border: dias === d ? '2px solid #2563eb' : '1px solid var(--border-color, #d1d5db)',
                backgroundColor: dias === d ? 'rgba(37,99,235,0.12)' : 'transparent',
                fontWeight: dias === d ? 700 : 400,
              }}
            >
              {d === 365 ? '1 ano' : `${d} dias`}
            </button>
          ))}
        </div>
        {ehAdmin && (
          <button type="button" className="btn-primario text-sm flex items-center gap-1" onClick={() => setNovo(true)}>
            <Plus size={14} /> Novo motivo
          </button>
        )}
      </div>

      {carregando ? (
        <p className="text-sm text-gray-400 py-6 text-center">Carregando...</p>
      ) : (
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
            <tr>
              <th className="text-left px-3 py-2 font-medium">Motivo</th>
              <th className="text-right px-3 py-2 font-medium w-28">Vendas</th>
              <th className="text-right px-3 py-2 font-medium w-32">Perdido</th>
              {/* Largura medida na tela publicada, nao chutada: os dois botoes somam ~191px e em
                  w-40 (160) o "Tirar da lista" quebrava em TRES linhas. */}
              {ehAdmin && <th className="px-3 py-2 w-56"></th>}
            </tr>
          </thead>
          <tbody>
            {linhas.map((m, idx) => (
              <tr
                key={m.id}
                className="border-b border-gray-100"
                style={{
                  backgroundColor: idx % 2 === 0 ? 'var(--grid-row-bg)' : 'var(--grid-row-alt-bg)',
                  color: 'var(--grid-row-fg)',
                  opacity: m.ativo ? 1 : 0.55,
                }}
              >
                <td className="px-3 py-2">
                  {editando?.id === m.id ? (
                    <input
                      autoFocus
                      value={editando.nome}
                      onChange={(e) => setEditando({ id: m.id, nome: upperMantendoCursor(e) })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void salvarNome();
                        if (e.key === 'Escape') setEditando(null);
                      }}
                      onBlur={() => void salvarNome()}
                      maxLength={60}
                      className="w-full px-2 py-1 text-sm border border-blue-500 rounded outline-none"
                      style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
                    />
                  ) : (
                    <>
                      {m.nome}
                      {m.exige_comentario && (
                        <span className="text-xs text-gray-500 ml-2">· pede comentario</span>
                      )}
                      {!m.ativo && (
                        <span className="text-xs ml-2 rounded-full px-2 py-0.5" style={{ backgroundColor: '#e2e8f0', color: '#000' }}>
                          fora de uso
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-bold">{m.usos}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatMoeda(Number(m.valor))}</td>
                {ehAdmin && (
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5 justify-end">
                      {/* `whiteSpace: nowrap` porque botao e' item de flex e encolhe ate o texto
                          caber: sem isso ele quebra por palavra dentro da propria celula. */}
                      <button
                        type="button"
                        className="btn-secundario text-xs px-2 py-1 inline-flex items-center gap-1"
                        style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
                        onClick={() => setEditando({ id: m.id, nome: m.nome })}
                      >
                        <Pencil size={12} /> Renomear
                      </button>
                      <button
                        type="button"
                        className="btn-secundario text-xs px-2 py-1"
                        style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
                        onClick={() => void alternarAtivo(m)}
                      >
                        {m.ativo ? 'Tirar da lista' : 'Devolver'}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="text-xs text-gray-500 mt-3">
        Motivo com zero venda ou nao acontece, ou ninguem o encontra na hora de marcar — as duas
        coisas sao decisao sua. Motivo tirado da lista some do botao "Perdeu" mas continua contando
        no que ja foi registrado.
      </p>

      <div className="mt-3">
        <button type="button" className="btn-secundario text-xs" onClick={() => void verComentarios()}>
          {comentarios ? 'Esconder comentarios' : 'Ver comentarios escritos'}
        </button>
        {comentarios && (
          <div className="mt-2 border-t border-gray-200 pt-2 max-h-52 overflow-y-auto">
            {comentarios.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">Nenhum comentario escrito no periodo.</p>
            ) : comentarios.map((c) => (
              <div key={c.numero + c.retomada_em} className="text-xs py-1.5 border-b border-gray-100">
                <span className="text-gray-500 font-bold">{c.numero}</span>
                <span className="text-gray-400"> · {c.motivo ?? 'sem motivo'} · {formatMoeda(Number(c.total))}</span>
                {c.vendedora && <span className="text-gray-400"> · {c.vendedora}</span>}
                <div>{c.comentario}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {novo && (
        <ModalCadastroRapido
          titulo="Novo motivo de perda"
          endpoint="/api/motivos-perda"
          campos={[{ key: 'nome', label: 'Motivo', obrigatorio: true }]}
          onFechar={() => setNovo(false)}
          onSalvo={() => { setNovo(false); void carregar(); }}
        />
      )}
    </Modal>
  );
}
